import * as React from 'react';
import { cx } from '../utils';
import type { StyledProps, WithChildren } from '../types';
import type { UnsafeProps } from '../escape-hatches';

export type LayerScopeSurface = 'ground' | 'container' | 'floating';
export type LayerScopeDescendants = 'auto' | 'raised' | 'floating';

export interface LayerScopeProps
  extends
    StyledProps,
    WithChildren,
    UnsafeProps,
    Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'className' | 'style'> {
  /**
   * Surface tier the scope represents. `container` matches a surface-1 custom
   * panel, `floating` matches popover/menu-like chrome, and `ground` is a
   * pass-through page/ground scope.
   */
  surface?: LayerScopeSurface;
  /**
   * Descendant tier recipe. `auto` chooses the Jacquard default for `surface`;
   * use `raised` or `floating` when a custom surface needs explicit descendant
   * headroom.
   */
  descendants?: LayerScopeDescendants;
  /**
   * When true, the wrapper paints the selected surface itself using a private
   * stash before descendant role tokens are rebound.
   */
  paint?: boolean;
}

export const LayerScope = React.forwardRef<HTMLDivElement, LayerScopeProps>(function LayerScope(
  {
    children,
    className,
    unsafe_className,
    unsafe_style,
    surface = 'container',
    descendants = 'auto',
    paint = false,
    ...rest
  },
  ref,
) {
  return (
    <div
      {...rest}
      ref={ref}
      className={cx('j-layer-scope', paint && 'j-layer-scope-painted', className, unsafe_className)}
      data-surface={surface}
      data-descendants={descendants}
      data-paint={paint ? 'true' : undefined}
      style={unsafe_style}
    >
      {children}
    </div>
  );
});
